import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ChatCircle, Briefcase, Package, Broadcast } from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { ShareBar } from "@/components/ShareBar";
import { VideoStrip } from "@/components/VideoStrip";
import { Avatar } from "@/components/Avatar";
import { PublicShell } from "@/components/PublicShell";

const TABS = [["all", "Everything"], ["posts", "From the floor"], ["jobs", "Vacancies"], ["listings", "For sale"]];
const money = (v, c) => `${c} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const KindTag = ({ kind }) => {
  const map = { post: ["From the floor", Broadcast], job: ["Vacancy", Briefcase], listing: ["For sale", Package] };
  const [text, Icon] = map[kind] || map.post;
  return (
    <span className="inline-flex items-center gap-1.5 border border-border bg-slate-50 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-slate-600">
      <Icon size={12} /> {text}
    </span>
  );
};

export default function PublicWall() {
  const [tab, setTab] = useState("all");
  const [data, setData] = useState(null);
  const [caption, setCaption] = useState(null);
  const origin = window.location.origin;

  const load = useCallback(async () => {
    const res = await api.get(`/public-site/wall?kind=${tab}&limit=40`);
    setData(res.data);
  }, [tab]);

  useEffect(() => { load().catch(() => setData({ items: [], counts: {} })); }, [load]);
  useEffect(() => {
    api.get("/feed/board/caption").then(({ data: d }) => setCaption(d)).catch(() => {});
  }, []);

  return (
    <PublicShell>
    <div data-testid="public-wall-page" className="bg-[#F7F8FA]">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 p-5 sm:px-8">
          <div>
            <p className="overline text-[#002FA7]">Unity &amp; Utility</p>
            <h1 className="font-display mt-1 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
              Live from the yard
            </h1>
            <p className="mt-2 max-w-xl text-sm text-slate-600">
              {data?.tagline || "Industrial floor operations, supply and logistics."}
            </p>
          </div>
          <div className="flex flex-col items-start gap-2">
            <ShareBar url={`${origin}/feed`} text={caption?.text || "Unity & Utility — live from the yard"}
              testid="wall-share" instagram
              youtube={{ title: "Live from the yard — Unity & Utility",
                description: caption?.text || "What is happening on our floor and on the road right now.",
                upload_url: "https://studio.youtube.com/channel/UCm0ahsoe5m2agd50-Ox4Uhg/videos" }} />
            <Link to="/login" data-testid="wall-signin-link" className="tap-safe text-sm font-semibold text-[#002FA7] hover:underline">
              Sign in to the platform
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl p-5 sm:px-8">
        <div className="mb-6 flex flex-wrap gap-2" data-testid="wall-tabs">
          {TABS.map(([key, text]) => (
            <button
              key={key}
              type="button"
              data-testid={`wall-tab-${key}`}
              onClick={() => setTab(key)}
              className={`border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors duration-150 ${tab === key ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-border bg-white text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]"}`}
            >
              {text}
            </button>
          ))}
        </div>

        {!data ? (
          <p className="py-16 text-center text-sm text-slate-500">Loading…</p>
        ) : data.items.length === 0 ? (
          <p data-testid="wall-empty" className="border border-border bg-white py-16 text-center text-sm text-slate-500">
            Nothing published yet — check back shortly.
          </p>
        ) : (
          <div className="space-y-4" data-testid="wall-items">
            {data.items.map((it) => {
              const link = it.token
                ? `${API.replace(/\/api$/, "")}/api/share/${it.kind === "job" ? "job" : "p"}/${it.token}`
                : `${API.replace(/\/api$/, "")}/api/share/feed`;
              return (
                <article key={`${it.kind}-${it.id}`} data-testid={`wall-item-${it.kind}-${it.id}`} className="border border-border bg-white p-4 transition-shadow duration-200 hover:shadow-[0_2px_16px_rgba(15,23,42,0.06)] sm:p-5">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <KindTag kind={it.kind} />
                    {it.kind === "post" && (
                      <Avatar imageId={it.author_avatar_id} sig={it.author_avatar_sig} name={it.author_name} size="sm" testid={`wall-avatar-${it.id}`} />
                    )}
                    <span className="text-sm font-semibold text-[#0F172A]">{it.title}</span>
                    {it.author_name && it.kind !== "post" && <span>{it.author_name}</span>}
                    {it.at && <span>{String(it.at).slice(0, 10)}</span>}
                  </div>

                  {it.body && <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">{it.body}</p>}

                  {it.kind === "job" && (
                    <p className="mt-2 text-xs text-slate-500" data-testid={`wall-job-meta-${it.id}`}>
                      {[it.location, it.country, it.employment_type, it.pay_label, it.closing_date ? `closes ${it.closing_date}` : null].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  {it.kind === "listing" && (
                    <p className="mt-2 text-sm font-semibold text-[#002FA7]" data-testid={`wall-listing-price-${it.id}`}>
                      {money(it.price, it.currency)}
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        {" · "}{[it.quantity ? `${it.quantity} ${it.unit || ""}`.trim() : null, it.country].filter(Boolean).join(" · ")}
                      </span>
                    </p>
                  )}

                  {(it.media || []).length > 0 && (
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {it.media.map((m) => (m.kind === "video" ? (
                        <video key={m.id} controls preload="metadata" data-testid={`wall-video-${m.id}`} className="w-full border border-border bg-black" src={`${API}/feed/media/${m.id}`} />
                      ) : (
                        <img key={m.id} alt={it.body || "post"} data-testid={`wall-image-${m.id}`} className="w-full border border-border object-cover" src={`${API}/feed/media/${m.id}`} />
                      )))}
                    </div>
                  )}
                  {it.kind === "listing" && it.image_url && (
                    <img src={`${API.replace(/\/api$/, "")}${it.image_url}`} alt={it.title} data-testid={`wall-listing-image-${it.id}`} className="mt-3 max-h-72 w-full border border-border object-cover" />
                  )}

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {it.kind === "post" && (
                        <>
                          <span><Heart size={13} className="mr-1 inline" />{it.like_count}</span>
                          <span><ChatCircle size={13} className="mr-1 inline" />{it.comment_count}</span>
                        </>
                      )}
                      {it.link && (
                        <Link to={it.link} data-testid={`wall-open-${it.kind}-${it.id}`} className="font-semibold text-[#002FA7] hover:underline">
                          {it.kind === "job" ? "See the vacancy" : "Open"}
                        </Link>
                      )}
                    </div>
                    <ShareBar url={link} text={it.title || "Unity & Utility"} testid={`wall-share-${it.kind}-${it.id}`} compact instagram />
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <VideoStrip limit={3} heading="Watch the yard at work" testid="wall-videos" />

        <footer className="mt-10 border-t border-border py-8 text-center text-xs text-slate-500">          <p>Follow the yard — share any post to Facebook, X, LinkedIn or WhatsApp with one tap.</p>
          <Link to="/login" className="mt-2 inline-block font-semibold text-[#002FA7] hover:underline">
            Staff and supplier sign-in
          </Link>
        </footer>
      </div>
    </div>
    </PublicShell>
  );
}
